// src/components/ui/Button.jsx
import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import Icon from "../AppIcon";

/**
 * Token-native button.
 * Heights:
 * - sm: 32px (dense / secondary)
 * - md (default): 40px (standard)
 * - lg: 48px (primary CTA, iOS-friendly)
 * - xl: 56px (hero)
 *
 * Enter-key behavior (global):
 * - Opt-in with `enterKey`
 * - Most recently mounted `enterKey` button wins
 * - Secondary buttons should NOT set `enterKey`
 */

// ───────────────────────────────────────────────
// Global Enter-key owner stack (module scope)
// ───────────────────────────────────────────────
const __enterOwners = [];
let __enterListenerBound = false;

function isEditableTarget(el) {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  if (tag === "textarea") return true;
  if (tag === "input") {
    const t = (el.getAttribute("type") || "text").toLowerCase();
    // allow Enter for most inputs; but never hijack in textarea (handled above)
    // treat these as editable too
    return !["button", "submit", "reset", "checkbox", "radio"].includes(t);
  }
  return !!el.isContentEditable;
}

function shouldHandleEnter(e) {
  if (e.key !== "Enter") return false;
  if (e.defaultPrevented) return false;
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return false;

  const active = document.activeElement;

  // Never hijack multiline typing
  if ((active?.tagName || "").toLowerCase() === "textarea") return false;

  // If focus is in an input/contenteditable, we allow ONLY if the owner exists.
  // (We still prevent weird submits by defaulting buttons to type="button".)
  if (isEditableTarget(active)) return true;

  // If focus is on body/other, still allow owner behavior.
  return true;
}

function bindGlobalEnterListenerOnce() {
  if (__enterListenerBound) return;
  __enterListenerBound = true;

  window.addEventListener(
    "keydown",
    (e) => {
      if (!shouldHandleEnter(e)) return;

      const owner = __enterOwners[__enterOwners.length - 1];
      if (!owner?.el) return;

      const btn = owner.el;

      // only click if it's clickable
      if (btn.disabled) return;
      if (btn.getAttribute("aria-disabled") === "true") return;

      // If inside a form, browser "submit" behavior can happen too.
      // We keep Button default type="button" to prevent double-trigger.
      e.preventDefault();
      btn.click();
    },
    true // capture so we win before random handlers
  );
}

function registerEnterOwner(id, el) {
  // remove any stale same-id entries
  for (let i = __enterOwners.length - 1; i >= 0; i--) {
    if (__enterOwners[i]?.id === id) __enterOwners.splice(i, 1);
  }
  __enterOwners.push({ id, el });
}

function unregisterEnterOwner(id) {
  for (let i = __enterOwners.length - 1; i >= 0; i--) {
    if (__enterOwners[i]?.id === id) __enterOwners.splice(i, 1);
  }
}

// ───────────────────────────────────────────────
// Styles
// ───────────────────────────────────────────────
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center",
    "rounded-xl font-medium",
    "transition disabled:opacity-50 disabled:cursor-not-allowed",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    "select-none",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))] hover:opacity-95 focus-visible:ring-[rgb(var(--afrimed-primary))] ring-offset-transparent",
        destructive:
          "bg-[rgb(var(--afrimed-error))] text-white hover:opacity-95 focus-visible:ring-[rgb(var(--afrimed-error))]",
        criticalTonal:
          "bg-[var(--state-critical-12)] text-[rgb(var(--schemes-critical))] hover:opacity-90 focus-visible:ring-[rgb(var(--schemes-critical))]",
        outline:
          "border border-[rgb(var(--afrimed-outline))] bg-transparent text-[rgb(var(--afrimed-foreground))] hover:bg-[rgb(var(--afrimed-muted))]",
        secondary:
          "bg-[rgb(var(--afrimed-secondary))] text-[rgb(var(--afrimed-on-secondary))] hover:opacity-95",
        tonal:
          "bg-[var(--state-primary-12)] text-[rgb(var(--afrimed-primary))] hover:bg-[var(--state-primary-16)] focus-visible:ring-[rgb(var(--afrimed-primary))]",
        text:
          "bg-transparent text-[rgb(var(--afrimed-primary))] hover:bg-[var(--state-primary-12)] focus-visible:ring-[rgb(var(--afrimed-primary))]",
        ghost:
          "bg-[rgba(86,106,139,0.08)] text-[rgb(var(--afrimed-primary))] hover:bg-[rgba(86,106,139,0.12)]",
        link:
          "p-0 h-auto min-h-0 text-[rgb(var(--afrimed-primary))] underline-offset-4 hover:underline",
        success: "bg-[rgb(var(--afrimed-success))] text-white hover:opacity-95",
        warning: "bg-[rgb(var(--afrimed-warning))] text-white hover:opacity-95",
        danger: "bg-[rgb(var(--afrimed-error))] text-white hover:opacity-95",
      },
      size: {
        sm: "min-h-8 px-3 text-[13px] leading-[18px]",
        default: "min-h-10 px-4 text-[14px] leading-[20px]",
        lg: "min-h-12 px-6 text-[14px] leading-[20px]",
        large: "min-h-11 px-5 text-[16px] leading-[20px]",
        cta: "min-h-12 px-6 text-[14px] leading-[20px]",
        xl: "min-h-14 px-7 text-[16px] leading-[24px]",
        icon: "min-h-10 min-w-10 px-0",
        xs: "min-h-7 px-2 text-[12px] leading-[16px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      loading = false,
      iconName = null,
      iconPosition = "left",
      iconSize = null,
      fullWidth = false,
      disabled = false,

      // ✅ NEW: Opt-in Enter ownership
      enterKey = false,

      // ✅ IMPORTANT: default to button so we don't create accidental submits
      type = "button",

      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    const localRef = React.useRef(null);
    React.useImperativeHandle(ref, () => localRef.current);

    // stable id per mount
    const ownerId = React.useMemo(
      () => `enter-owner-${Math.random().toString(36).slice(2)}`,
      []
    );

    React.useEffect(() => {
      if (!enterKey) return;

      bindGlobalEnterListenerOnce();

      const el = localRef.current;
      if (el) registerEnterOwner(ownerId, el);

      return () => unregisterEnterOwner(ownerId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enterKey]);

    const iconSizeMap = {
      xs: 12,
      sm: 14,
      default: 16,
      lg: 18,
      large: 18,
      cta: 18,
      xl: 20,
      icon: 16,
    };
    const computedIconSize = iconSize || iconSizeMap?.[size] || 16;

    const LoadingSpinner = () => (
      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    const renderIcon = () =>
      iconName ? (
        <Icon
          name={iconName}
          size={computedIconSize}
          className={cn(
            children && iconPosition === "left" && "mr-2",
            children && iconPosition === "right" && "ml-2"
          )}
        />
      ) : null;

    const classes = cn(
      buttonVariants({ variant, size }),
      fullWidth && "w-full",
      className
    );

    return (
      <Comp
        ref={localRef}
        type={asChild ? undefined : type}
        className={classes}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <LoadingSpinner />}
        {iconName && iconPosition === "left" && renderIcon()}
        {children}
        {iconName && iconPosition === "right" && renderIcon()}
      </Comp>
    );
  }
);

Button.displayName = "Button";
export default Button;
